import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { MainMenuScene } from "./MainMenuScene";
import { CHAMPIONS, TIER_COLORS } from "../data/champions";
import { championIconUrl } from "../config/constants";
import { levelForXp } from "../units/UnitStats";
import { recordWin } from "../game/RunHistory";
import type { RosterEntry } from "../game/RunState";
import { getDifficultyConfig } from "../data/difficulty";

export class VictoryScene implements Scene {
  private root!: HTMLDivElement;

  constructor(private game: Game) {}

  enter(): void {
    const state = this.game.runState;
    if (state) recordWin(state.roster, state.difficulty);
    this.game.clearSave();

    this.root = document.createElement("div");
    this.root.style.cssText =
      "position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;" +
      "justify-content:center;gap:20px;padding:24px;overflow-y:auto;" +
      "background:radial-gradient(ellipse at 50% 30%, rgba(240,200,60,0.08) 0%, rgba(10,14,20,0) 70%);";

    // Banner
    const banner = document.createElement("div");
    banner.style.cssText =
      "font-size:clamp(28px,6vw,52px);font-weight:900;letter-spacing:4px;text-align:center;" +
      "color:#f0e040;text-shadow:0 0 40px #f0e04099,0 0 80px #c8a02055;line-height:1.1;";
    banner.textContent = "RUNETERRA CONQUERED";
    this.root.appendChild(banner);

    const sub = document.createElement("div");
    sub.style.cssText =
      "color:rgba(180,190,210,0.65);font-size:13px;text-align:center;max-width:320px;margin-top:-8px;";
    sub.textContent = "The peak of Targon falls silent. Your legend echoes across every region.";
    this.root.appendChild(sub);

    if (state && state.difficulty !== "normal") {
      const cfg = getDifficultyConfig(state.difficulty);
      const isImpossible = state.difficulty === "impossible";
      const badge = document.createElement("div");
      badge.style.cssText =
        `font-size:11px;font-weight:700;letter-spacing:3px;color:${isImpossible ? "#ff2828" : "#ff8848"};` +
        `text-shadow:0 0 12px ${isImpossible ? "#ff282899" : "#ff884888"};margin-top:-4px;`;
      badge.textContent = `${cfg.label} MODE`;
      this.root.appendChild(badge);
    }

    // Team display
    if (state?.roster.length) {
      const teamLabel = document.createElement("div");
      teamLabel.style.cssText =
        "font-size:10px;letter-spacing:3px;color:rgba(200,170,100,0.6);margin-bottom:-8px;";
      teamLabel.textContent = "WINNING TEAM";
      this.root.appendChild(teamLabel);

      const teamRow = document.createElement("div");
      teamRow.style.cssText =
        "display:flex;gap:16px;align-items:flex-start;justify-content:center;flex-wrap:wrap;";
      for (const entry of state.roster) {
        teamRow.appendChild(this.buildChampCard(entry));
      }
      this.root.appendChild(teamRow);
    }

    // Divider
    const divider = document.createElement("div");
    divider.style.cssText = "width:160px;height:1px;background:rgba(200,170,100,0.2);";
    this.root.appendChild(divider);

    const btn = document.createElement("button");
    btn.className = "btn";
    btn.textContent = "RETURN TO MENU";
    btn.addEventListener("click", () => {
      this.game.runState = null;
      this.game.sceneManager.transition(new MainMenuScene(this.game));
    });
    this.root.appendChild(btn);

    this.game.sceneRoot.appendChild(this.root);
  }

  private buildChampCard(entry: RosterEntry): HTMLDivElement {
    const def = CHAMPIONS[entry.championId];
    const level = levelForXp(entry.xp);
    const stars = entry.stars ?? 1;
    const tierColor = TIER_COLORS[def.tier];
    const starColor = stars === 3 ? "#ffe066" : stars === 2 ? "#f0c040" : tierColor;
    const ringColor = stars >= 2 ? starColor : tierColor;

    const card = document.createElement("div");
    card.style.cssText =
      "display:flex;flex-direction:column;align-items:center;gap:6px;";

    const icon = document.createElement("div");
    icon.style.cssText =
      `width:72px;height:72px;border-radius:8px;border:2px solid ${ringColor};` +
      `background-image:url('${championIconUrl(def.iconId)}');background-size:cover;background-position:center;` +
      `box-shadow:0 0 ${stars >= 2 ? "14px" : "6px"} ${ringColor}66;flex-shrink:0;`;
    card.appendChild(icon);

    const name = document.createElement("div");
    name.style.cssText =
      `font-size:11px;font-weight:700;color:${tierColor};text-align:center;max-width:80px;`;
    name.textContent = def.displayName;
    card.appendChild(name);

    const info = document.createElement("div");
    info.style.cssText =
      `font-size:10px;color:${stars >= 2 ? starColor : "rgba(180,190,210,0.6)"};text-align:center;`;
    info.textContent = `Lv${level}${stars > 1 ? " " + "★".repeat(stars) : ""}`;
    card.appendChild(info);

    return card;
  }

  exit(): void {
    this.root?.remove();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}
}
